/**
 * Returns a function that as long as it continues to be invoked, won't be triggered.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Delay (in milliseconds).
 * @returns {Function}
 */
function debounce(fn, wait = 300) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

if (!customElements.get('announcement-bar')) {
  class AnnouncementBar extends HTMLElement {
    constructor() {
      super();
      this.slider = this.querySelector('.announcement__slider');
      this.localization = this.querySelector('.announcement__localization');
      this.links = this.querySelectorAll('.js-announcement-link');
      this.prevBtn = this.querySelector('.announcement__arrow--prev');
      this.nextBtn = this.querySelector('.announcement__arrow--next');
      this.menu = document.querySelector('.main-menu__content');
      this.isPaused = false;
      this.currentIndex = 0;

      this.bindEvents();
      this.init();
    }

    disconnectedCallback() {
      if (this.moveLinksHandler) {
        window.removeEventListener('on:breakpoint-change', this.moveLinksHandler);
      }

      if (this.moveLocalizationHandler) {
        window.removeEventListener('on:breakpoint-change', this.moveLocalizationHandler);
      }

      clearTimeout(this.slideTimer);
    }

    bindEvents() {
      if (this.links) {
        this.moveLinksHandler = this.moveLinksHandler || this.moveLinks.bind(this);
        window.addEventListener('on:breakpoint-change', this.moveLinksHandler);
      }

      if (this.localization) {
        this.moveLocalizationHandler = this.moveLocalizationHandler
          || debounce(this.moveLocalization.bind(this));
        window.addEventListener('on:breakpoint-change', this.moveLocalizationHandler);
      }

      if (this.slider) {
        this.slider.addEventListener('mouseenter', this.pauseSlider.bind(this));
        this.slider.addEventListener('mouseleave', this.resumeSlider.bind(this));

        if (this.prevBtn) {
          this.prevBtn.addEventListener('click', () => this.step(-1));
        }

        if (this.nextBtn) {
          this.nextBtn.addEventListener('click', () => this.step(1));
        }
      }
    }

    /**
     * Start sliders a slidin'
     */
    init() {
      if (this.slider) {
        this.slides = Array.from(this.slider.querySelectorAll('.announcement__text'));
        this.slides[0].classList.add('is-visible');

        this.queueNextSlide();
      }

      if (this.links) {
        this.moveLinks();
      }

      if (this.localization) {
        this.moveLocalization();
      }
    }

    /**
     * Schedules the next automatic slide change, replacing any pending one.
     */
    queueNextSlide() {
      clearTimeout(this.slideTimer);
      this.slideTimer = setTimeout(() => {
        if (!this.isPaused && theme.elementUtil.isInViewport(this)) {
          this.step(1);
        } else {
          this.queueNextSlide();
        }
      }, this.dataset.slideDelay);
    }

    /**
     * Moves the slider by the given number of slides, wrapping around at both ends.
     * @param {number} direction - Number of slides to advance by (negative to go back).
     */
    step(direction) {
      const total = this.slides.length;
      this.goToSlide((this.currentIndex + direction + total) % total);
    }

    /**
     * Scrolls to a slide and marks it as the visible one.
     * @param {number} index - Index of the slide to show.
     */
    goToSlide(index) {
      const currSlide = this.slides[this.currentIndex];
      const nextSlide = this.slides[index];

      // offsetLeft deltas are already signed correctly in both LTR and RTL.
      this.slider.scrollTo({
        left: nextSlide.offsetLeft - this.slides[0].offsetLeft,
        behavior: 'smooth'
      });

      currSlide.classList.remove('is-visible');
      nextSlide.classList.add('is-visible');
      this.currentIndex = index;

      this.queueNextSlide();
    }

    /**
     * Move the help links selectors to the mobile nav/back to the announcement bar if necessary
     */
    moveLinks() {
      const menuAnnouncementLinks = document.querySelector('.mob__announcement-links');
      if (!theme.mediaMatches.md && !menuAnnouncementLinks) {
        // Move localization to mobile
        const mobNav = document.createElement('nav');
        mobNav.classList.add('mob__announcement-links');

        const mobNavUl = document.createElement('ul');
        mobNav.classList.add('secondary-nav');

        this.links.forEach((link) => {
          mobNav.innerHTML += `<li><a class="secondary-nav__item" href="${link.href}">${link.innerText}</a>`;
        });

        mobNav.appendChild(mobNavUl);
        this.menu.appendChild(mobNav);
      } else if (theme.mediaMatches.md && menuAnnouncementLinks) {
        menuAnnouncementLinks.remove();
      }
    }

    /**
     * Move the localization selectors to the mobile nav/back to the announcement bar if necessary
     */
    moveLocalization() {
      this.slider = this.querySelector('.announcement__slider');
      this.localization = this.querySelector('.announcement__localization');
      this.links = this.querySelectorAll('.js-announcement-link');
      this.menu = document.querySelector('.main-menu__content');
      const menuLocalization = document.querySelector('.mob__localization');
      if (!theme.mediaMatches.md && !menuLocalization) {
        // Move localization to mobile
        const mobLocalizationElem = document.createElement('div');
        mobLocalizationElem.classList.add('mob__localization');
        mobLocalizationElem.appendChild(this.localization.firstElementChild);
        this.menu.appendChild(mobLocalizationElem);
      } else if (theme.mediaMatches.md && menuLocalization) {
        // Move localization back to announcement bar
        this.localization.appendChild(menuLocalization.firstElementChild);
        menuLocalization.remove();
      }
    }

    /**
     * Pauses the auto-rotating slider
     */
    pauseSlider() {
      this.isPaused = true;
    }

    /**
     * Resumes the auto-rotating slider
     */
    resumeSlider() {
      this.isPaused = false;
    }
  }

  customElements.define('announcement-bar', AnnouncementBar);
}
